import { Middleware } from '@reduxjs/toolkit';

import { invalidateAll, QaCacheState } from './qaCacheSlice';
import { persistQaCacheDebounced } from './qaCacheStorage';

/**
 * Middleware that flushes QA cache entries when task mutations occur.
 *
 * Why:
 *   When a user completes, reassigns, or otherwise mutates a task the cached
 *   QA data becomes stale. For MVP we do a "nuclear" invalidation — clear
 *   all entries. Targeted invalidation can follow in v2.
 *
 * How:
 *   Listens for fulfilled mutation actions whose type contains task-mutation
 *   endpoint names and dispatches `invalidateAll` when one fires.
 */
const TASK_MUTATION_PATTERNS = [
  'updateTaskComplete',
  'assignStaffToTask',
  'assignProvider',
  'assignTaskToTeam',
  'changeDisposition',
  'createNewTask',
  'escalateTask',
  'deescalateTask'
];

const isTaskMutationFulfilled = (actionType: string): boolean =>
  actionType.includes('/executeMutation/fulfilled') &&
  TASK_MUTATION_PATTERNS.some((pattern) => actionType.includes(pattern));

export const qaCacheMiddleware: Middleware = (storeApi) => (next) => (action) => {
  const result = next(action);

  const actionType =
    typeof action === 'object' && action !== null && 'type' in action
      ? (action as { type: string }).type
      : '';

  if (isTaskMutationFulfilled(actionType)) {
    storeApi.dispatch(invalidateAll());
  }

  const state = storeApi.getState();
  const qaCacheState = state.qaCache as QaCacheState | undefined;
  const userId = state.user?._id as string | undefined;

  if (qaCacheState && userId && actionType.startsWith('qaCache/')) {
    persistQaCacheDebounced(userId, qaCacheState);
  }

  return result;
};
